// ABOUTME: Sidebar breadcrumb of the selected node's ancestors: root first, then the parent,
// ABOUTME: with the levels in between collapsed behind a "..." button that opens a menu.
import {
    AccountTreeOutlinedIcon,
    Box,
    IconButton,
    KeyboardArrowRightIcon as BreadcrumbSeparator,
    Menu,
    MenuItem,
    MoreHorizOutlinedIcon,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { BreadcrumbNode } from '@src/__generated__/types';
import { ReactElement, useState } from 'react';
import { useIntl } from 'react-intl';

import { BreadcrumbClickFn } from './types';

type BreadcrumbProps = {
    breadcrumb: BreadcrumbNode[];
    onNodeClick: BreadcrumbClickFn;
    /** Shown as a leading icon button (the hierarchies icon) that goes back to the hierarchies list. */
    onRootClick?: () => void;
    /** Icon and label of that button, for trails that lead back somewhere else. */
    rootIcon?: ReactElement;
    rootLabel?: string;
};

const nodeLabel = (node: BreadcrumbNode) => node.listName ?? node.code;

export function Breadcrumb(props: BreadcrumbProps) {
    const { breadcrumb, onNodeClick, onRootClick, rootIcon, rootLabel } = props;
    const intl = useIntl();

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const rootTitle =
        rootLabel ?? intl.formatMessage({ id: 'hierarchies', defaultMessage: 'Hierarchies' });

    const [rootNode] = breadcrumb;
    // The last entry is the selected node itself: the caller shows it below the trail.
    const ancestors = breadcrumb.slice(0, -1);
    const collapsed = ancestors.length > 2;
    const intermediateNodes = collapsed ? ancestors.slice(1, -1) : [];
    const visibleNodes = collapsed ? [ancestors[0], ancestors[ancestors.length - 1]] : ancestors;

    const navigateTo = (nodeId: string) => {
        setMenuAnchor(null);
        onNodeClick(rootNode.id, nodeId);
    };

    // Ancestors fade to secondary text; the parent, last in the trail, stays primary text.
    const renderNode = (node: BreadcrumbNode, isLast: boolean) => (
        <Box key={node.id} cursor="pointer" onClick={() => navigateTo(node.id)}>
            <Typography variant="body2" color={isLast ? 'text.primary' : 'text.secondary'}>
                {nodeLabel(node)}
            </Typography>
        </Box>
    );

    return (
        <>
            <Breadcrumbs
                separator={<BreadcrumbSeparator fontSize="small" color="action" />}
                aria-label="breadcrumb"
                sx={{ marginBottom: '1.25rem' }}
            >
                {onRootClick && breadcrumb.length > 0 && (
                    <Tooltip key="hierarchies" title={rootTitle}>
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label={rootTitle}
                            onClick={onRootClick}
                        >
                            {rootIcon ?? <AccountTreeOutlinedIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}
                {visibleNodes.map((node, index) => {
                    const isLast = index === visibleNodes.length - 1;
                    return collapsed && index === 1
                        ? [
                              <Box
                                  key="intermediate-levels"
                                  display="flex"
                                  alignItems="center"
                                  px={1}
                                  py={0.25}
                                  borderRadius={1.5}
                                  bgcolor="grey.100"
                                  cursor="pointer"
                                  aria-label="intermediate levels"
                                  onClick={e => setMenuAnchor(e.currentTarget)}
                              >
                                  <MoreHorizOutlinedIcon fontSize="small" color="action" />
                              </Box>,
                              renderNode(node, isLast),
                          ]
                        : renderNode(node, isLast);
                })}
            </Breadcrumbs>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                {intermediateNodes.map(node => (
                    <MenuItem key={node.id} onClick={() => navigateTo(node.id)}>
                        {nodeLabel(node)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
